import React, { Fragment, ReactNode } from 'react'
import { ChatBubblesIcon, LabFlaskIcon } from 'src/designSystem/icons'
import { AdvancedPatientSummary } from '../api/types'
import LabResultsIndicator from '../dashboard/identifiers/LabResultsIndicator'
import UnreadCount from '../dashboard/chat/UnreadCount'
import { CaseloadView } from './caseloadView'
import { CaseloadParticipant } from './useAdvancedCaseloadParticipants'

type PatientContext = {
  summary: AdvancedPatientSummary
  participant?: CaseloadParticipant
}

type IndicatorContext = PatientContext & {
  expanded: boolean
}

export type CaseloadFilter = {
  key: string
  label: string
  icon: ReactNode
  matches: (context: PatientContext) => boolean
}

type CaseloadIndicator = {
  key: string
  // `name` draws before the patient's name, `chips` among the row's chips.
  slot: 'name' | 'chips'
  // Shown in every view when left out.
  views?: CaseloadView[]
  // Null when there is nothing to show for this patient.
  render: (context: IndicatorContext) => ReactNode
  // Offers a filter pill above the caseload, in the same views as the indicator.
  filter?: Omit<CaseloadFilter, 'key'>
}

export const hasUnviewedLabResults = (latestReturnedLabOrderID: string | null, viewedLabOrderIds: string[] = []) =>
  latestReturnedLabOrderID !== null && !viewedLabOrderIds.includes(latestReturnedLabOrderID)

const unreadCareTeamChat = ({ summary }: PatientContext) => summary.indicators?.careTeamChatUnread?.count ?? 0

const labResultsAwaiting = ({ summary, participant }: PatientContext) =>
  hasUnviewedLabResults(participant?.latestReturnedLabOrderID ?? null, summary.indicators?.labResultViews?.orderIds)

// A service-backed indicator reads `summary.indicators[key]`, which the service's registry fills; one with no
// service half draws from the summary or `server`'s participant alone. Add the entry here, nothing else.
const CASELOAD_INDICATORS: CaseloadIndicator[] = [
  {
    key: 'careTeamChatUnread',
    slot: 'name',
    render: (context) => <UnreadCount count={unreadCareTeamChat(context)} />,
    filter: {
      label: 'Care team chat',
      icon: <ChatBubblesIcon />,
      matches: (context) => unreadCareTeamChat(context) > 0,
    },
  },
  {
    key: 'labResultViews',
    slot: 'chips',
    views: ['all', 'psychiatrist'],
    render: (context) => (labResultsAwaiting(context) ? <LabResultsIndicator expanded={context.expanded} /> : null),
    filter: { label: 'Lab results', icon: <LabFlaskIcon />, matches: labResultsAwaiting },
  },
]

const showsIn = (indicator: CaseloadIndicator, view: CaseloadView) => !indicator.views || indicator.views.includes(view)

export const renderCaseloadIndicators = (
  slot: CaseloadIndicator['slot'],
  view: CaseloadView,
  context: IndicatorContext
) =>
  CASELOAD_INDICATORS.filter((indicator) => indicator.slot === slot && showsIn(indicator, view)).map((indicator) => (
    <Fragment key={indicator.key}>{indicator.render(context)}</Fragment>
  ))

export const caseloadFilters = (view: CaseloadView): CaseloadFilter[] =>
  CASELOAD_INDICATORS.flatMap((indicator) =>
    indicator.filter && showsIn(indicator, view) ? [{ key: indicator.key, ...indicator.filter }] : []
  )
